// src/components/staffing/TeamConfigurationManager.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Logger } from '../../utils/logger';
import { COLOR_GOOD, COLOR_WARN, COLOR_POOR, BG_ELEVATED, BG_SURFACE } from '../../utils/colors';
import { apiGet, apiFetch } from '../../utils/api';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider,
  Snackbar,
  Checkbox
} from '@mui/material';
import {
  Save as SaveIcon,
  Compare as CompareIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  TrendingUp as PerformanceIcon,
  AttachMoney as CostIcon,
  Speed as SpeedIcon,
  Security as RiskIcon,
  BookmarkAdd as BookmarkIcon,
  Psychology as CustomIcon,

} from '@mui/icons-material';
import CandidateDetailsModal from './CandidateDetailsModal';
import AIDecisionAdvisor from './AIDecisionAdvisor';
import { generateComparisonData } from './comparison-utils';

const logger = new Logger('TeamConfigurationManager');

/**
 * TeamConfigurationManager - Manage multiple team configurations
 */
const TeamConfigurationManager = ({ 
  staffingData, 
  optimizationResult,
  onConfigurationLoad,
  initialActiveSlot = 0
}) => {
  // State management
  const [configurations, setConfigurations] = useState([]);
  const [activeConfigSlot, setActiveConfigSlot] = useState(initialActiveSlot); // 0=A, 1=B, 2=C, 3=D, 4=E
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Save dialog state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [configurationName, setConfigurationName] = useState('');
  const [optimizationGoal, setOptimizationGoal] = useState('performance_maximization');

  // Comparison selection state
  const [selectedForComparison, setSelectedForComparison] = useState([]);
  
  // Comparison state
  const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [isComparing, setIsComparing] = useState(false);

  // Team member details modal state
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [teamMemberDetailsOpen, setTeamMemberDetailsOpen] = useState(false);

  // Snackbar notification state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Confirmation dialog state for overwriting
  const [overwriteConfirmOpen, setOverwriteConfirmOpen] = useState(false);
  const [slotToOverwrite, setSlotToOverwrite] = useState(null);

  // Confirmation dialog state for deleting
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState(null);

  // Available optimization goals
  const optimizationGoals = [
    { value: 'performance_maximization', label: 'Performance Maximization', icon: <PerformanceIcon /> },
    { value: 'cost_efficiency', label: 'Cost Efficiency', icon: <CostIcon /> },
    { value: 'risk_minimization', label: 'Risk Minimization', icon: <RiskIcon /> },
    { value: 'speed_to_market', label: 'Speed to Market', icon: <SpeedIcon /> },
    { value: 'custom_balance', label: 'Custom Balance', icon: <CustomIcon /> }
  ];

  // Get assigned team members
  const getAssignedTeamMembers = () => {
    if (!staffingData?.roles) return [];

    // Get margin analysis data from optimization result if available
    const marginData = optimizationResult?.components?.margin_analysis?.individual_margins || [];

    return staffingData.roles
      .filter(role => role.status === 'assigned' && role.assigned_employee_id)
      .map(role => {
        // Find the candidate data to get availability and cost information
        const candidateData = role.candidates?.find(c => c.employee_id === role.assigned_employee_id);

        // Find margin data for this employee to get enriched cost information
        const employeeMarginData = marginData.find(m =>
          m.employee_name === role.assigned_employee_name ||
          m.role === role.role_name
        );

        // Parse hourly_rate from formatted string (e.g., "$82.94/hr") or use raw number
        let hourly_rate = null; // No default - data must exist
        if (role.assigned_hourly_rate) {
          if (typeof role.assigned_hourly_rate === 'string') {
            // Extract number from formatted string like "$82.94/hr"
            const match = role.assigned_hourly_rate.match(/\$?(\d+(?:\.\d+)?)/);
            if (match) {
              hourly_rate = parseFloat(match[1]);
            }
          } else {
            hourly_rate = role.assigned_hourly_rate;
          }
        } else if (role.hourly_rate) {
          hourly_rate = role.hourly_rate;
        }

        // ✅ FIX: Extract cost and billing data from optimization result first, then candidate - NO FALLBACKS
        const employee_cost_rate = employeeMarginData?.cost_rate ||
                                    candidateData?.employee_cost_rate ||
                                    candidateData?.cost_rate ||
                                    candidateData?.hourly_cost ||
                                    hourly_rate ||
                                    null;
        const role_billing_rate = employeeMarginData?.billing_rate ||
                                   role.hourly_rate ||
                                   role.billing_rate ||
                                   null;
        const allocated_hours = employeeMarginData?.allocated_hours ||
                                 role.total_hours ||
                                 role.estimated_hours ||
                                 null;

        return {
          employee_id: role.assigned_employee_id,
          employee_name: role.assigned_employee_name,
          role: role.role_name,
          role_id: role.id,
          fit_score: role.fit_score || 8.0,
          assigned_tech_score: role.assigned_tech_score || role.fit_score || 8.0,
          assignment_confidence: 0.9,
          hourly_rate: hourly_rate,
          // ✅ FIX: Include cost and billing fields for correct display on reload
          employee_cost_rate: employee_cost_rate,
          role_billing_rate: role_billing_rate,
          allocated_hours: allocated_hours,
          // Capture availability data from candidate info
          availability_tier: candidateData?.availability?.tier || candidateData?.availability_tier,
          next_available_date: candidateData?.availability?.next_available_date || candidateData?.next_available_date,
          available_date: candidateData?.available_date,
          available_from: candidateData?.available_from,
          csot_8_weeks: candidateData?.csot_8_weeks,
          availability_status: candidateData?.availability?.status
        };
      });
  };

  // Load configurations for the project
  const loadConfigurations = async () => {
    if (!staffingData?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      logger.debug('📋 Loading configurations for project:', staffingData.id);
      
      const response = await apiGet(
        `team-optimization/configurations/project/${staffingData.id}`
      );

      if (response.status === 'success') {
        const configs = response.configurations || [];
        logger.debug('✅ Loaded configurations:', configs);

        // Debug: Log each config's booking_status and margin data
        configs.forEach((config, index) => {
          logger.debug(`Loaded config ${index}: ${config.configuration_name}, booking_status: ${config.booking_status}`);
          logger.debug(`  💰 Margin data - margin_percentage: ${config.margin_percentage}, comparison_metrics.margin_percentage: ${config.comparison_metrics?.margin_percentage}`);
        });

        // Show named lineups (slots 1-5) and explicit user-created drafts (slot 0 + is_partial_draft)
        setConfigurations(configs.filter(c =>
          (c.configuration_slot || 0) > 0 ||
          c.is_partial_draft === true
        ));
      } else {
        throw new Error(response.message || 'Failed to load configurations');
      }
    } catch (err) {
      logger.error('❌ Error loading configurations:', err);
      setError(`Failed to load configurations: ${err.message}`);
      setConfigurations([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load configurations on mount and when project changes
  useEffect(() => {
    loadConfigurations();
  }, [staffingData?.id]);

  // Auto-populate configuration name when save dialog opens
  useEffect(() => {
    if (saveDialogOpen && !configurationName) {
      const defaultName = generateDefaultConfigName();
      if (defaultName) {
        setConfigurationName(defaultName);
      }
    }
  }, [saveDialogOpen]);

  // Save current team as configuration
  const handleSaveConfiguration = async () => {
    const teamMembers = getAssignedTeamMembers();

    if (teamMembers.length === 0) {
      setError('No team members assigned. Please assign team members before saving.');
      showNotification('No team members assigned. Please assign team members before saving.', 'error');
      return;
    }

    if (!configurationName.trim()) {
      setError('Please enter a configuration name.');
      showNotification('Please enter a configuration name.', 'error');
      return;
    }

    // Check if slot is occupied and show confirmation dialog
    if (configurations[activeConfigSlot] !== null) {
      setSlotToOverwrite(activeConfigSlot);
      setOverwriteConfirmOpen(true);
      return;
    }

    // Proceed with save
    await performSaveConfiguration();
  };

  // Perform the actual save operation
  const performSaveConfiguration = async () => {
    const teamMembers = getAssignedTeamMembers();

    setIsSaving(true);
    setError(null);

    try {
      logger.debug('💾 Saving configuration to slot:', activeConfigSlot, '(1-based:', activeConfigSlot + 1, ')');
      logger.debug('💾 Configuration name:', configurationName);

      // Calculate combined experience score: Client (50%) + Industry (30%) + Collaboration (20%)
      let calculatedExperienceScore = 7.5; // Default
      if (optimizationResult?.components) {
        const industryAvg = optimizationResult.components.industry_client_experience?.industry_average || 7.5;
        const clientAvg = optimizationResult.components.industry_client_experience?.client_average || 7.5;
        const collaborationScore = optimizationResult.components.collaboration_history?.score || 0;
        const collaborationAvailable = optimizationResult.components.collaboration_history?.data_available || false;
        const effectiveCollaborationScore = collaborationAvailable ? collaborationScore : ((industryAvg + clientAvg) / 2);
        calculatedExperienceScore = (clientAvg * 0.5) + (industryAvg * 0.3) + (effectiveCollaborationScore * 0.2);
      }

      // ✅ FIX: Save the COMPLETE components object with all detailed data
      const optimizationAnalysis = optimizationResult ? {
        overall_score: optimizationResult.overall_score || 0,
        technical_coverage: optimizationResult.components?.technical_coverage?.score || 0,
        experience_balance: calculatedExperienceScore, // Use calculated combined experience score
        collaboration_score: optimizationResult.components?.collaboration_score?.score || 0,
        disc_compatibility: optimizationResult.components?.disc_compatibility?.score || 0,
        strengths_synergy: optimizationResult.components?.strengths_synergy?.score || 0,
        risk_assessment: optimizationResult.components?.risk_assessment?.risk_level || 'medium',
        predicted_success_rate: optimizationResult.success_probability || 0,
        confidence_interval: [0.0, 1.0],
        // ✅ CRITICAL: Save complete component details for restoration
        full_components: optimizationResult.components
      } : {
        overall_score: 8.0,
        technical_coverage: 8.0,
        experience_balance: 7.5,
        collaboration_score: 8.2,
        disc_compatibility: 7.8,
        strengths_synergy: 7.6,
        risk_assessment: 'low',
        predicted_success_rate: 0.85,
        confidence_interval: [0.0, 1.0]
      };
      
      // Debug: Check what margin_analysis data we have
      logger.debug('💰 Optimization result margin_analysis:', {
        exists: !!optimizationResult?.components?.margin_analysis,
        total_cost: optimizationResult?.components?.margin_analysis?.total_cost,
        total_revenue: optimizationResult?.components?.margin_analysis?.total_revenue,
        overall_margin: optimizationResult?.components?.margin_analysis?.overall_margin,
        full_margin_data: optimizationResult?.components?.margin_analysis
      });

      // Debug: Check what team member cost data we have
      logger.debug('👥 Team members cost data:', teamMembers.map(m => ({
        name: m.employee_name,
        role: m.role,
        hourly_rate: m.hourly_rate,
        employee_cost_rate: m.employee_cost_rate,
        role_billing_rate: m.role_billing_rate,
        allocated_hours: m.allocated_hours
      })));

      // Prepare comparison metrics with enhanced financial data
      const comparisonMetrics = {
        estimated_cost: optimizationResult?.components?.margin_analysis?.total_cost || 0,
        estimated_revenue: optimizationResult?.components?.margin_analysis?.total_revenue || 0,
        margin_percentage: optimizationResult?.components?.margin_analysis?.overall_margin || 0,
        estimated_duration_weeks: optimizationResult?.components?.margin_analysis?.project_duration_weeks || 12,
        default_hours_used: optimizationResult?.components?.margin_analysis?.default_hours_used || false,
        risk_factors: optimizationResult?.components?.risk_assessment?.identified_risks || [],
        strengths: ['Strong technical coverage', 'Good collaboration history'],
        skill_coverage_percentage: optimizationResult?.components?.technical_coverage?.coverage_percentage || 85.0,
        team_chemistry_score: optimizationResult?.components?.collaboration_score?.score || 8.2,
        performance_prediction: (optimizationResult?.success_probability || 0.85) * 10
      };

      // Create final config payload with financial data at top level for easier access
      const configPayload = {
        project_id: staffingData.id,
        configuration_name: configurationName,
        optimization_goal: optimizationGoal,
        configuration_slot: activeConfigSlot, // Direct mapping: 0=auto-save, 1-5=A-E
        team_members: teamMembers,
        optimization_analysis: optimizationAnalysis,
        comparison_metrics: comparisonMetrics,
        // ✅ Add financial data at top level for easier access (matches TeamOptimizationDashboard)
        estimated_revenue: optimizationResult?.components?.margin_analysis?.total_revenue || 0,
        estimated_cost: optimizationResult?.components?.margin_analysis?.total_cost || 0,
        margin_percentage: optimizationResult?.components?.margin_analysis?.overall_margin || 0,
        default_hours_used: optimizationResult?.components?.margin_analysis?.default_hours_used || false,
        claude_analysis: optimizationResult?.claude_analysis || '',
        detailed_analysis: optimizationResult?.detailed_analysis || '',
        user_notes: `Saved on ${new Date().toLocaleDateString()}`
      };

      logger.debug('💾 Saving configuration payload:', {
        hasFullComponents: !!optimizationAnalysis.full_components,
        fullComponentsKeys: optimizationAnalysis.full_components ? Object.keys(optimizationAnalysis.full_components) : [],
        hasClaudeAnalysis: !!configPayload.claude_analysis,
        hasDetailedAnalysis: !!configPayload.detailed_analysis,
        claudeAnalysisLength: configPayload.claude_analysis.length,
        detailedAnalysisLength: configPayload.detailed_analysis.length
      });

      const response = await apiFetch(
        'team-optimization/configurations',
        {
          method: 'POST',
          body: JSON.stringify(configPayload)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'success') {
        logger.debug('✅ Configuration saved successfully');
        setSaveDialogOpen(false);
        setConfigurationName('');
        await loadConfigurations(); // Reload configurations
        showNotification(`Configuration saved successfully to Slot ${getSlotLetter(activeConfigSlot)}`, 'success');
      } else {
        throw new Error(data.message || 'Save failed');
      }

    } catch (err) {
      logger.error('❌ Error saving configuration:', err);
      const errorMsg = err.message.includes('occupied') || err.message.includes('exists')
        ? `Cannot save: Slot ${getSlotLetter(activeConfigSlot)} is already occupied. Please choose a different slot or delete the existing configuration first.`
        : err.message.includes('members')
        ? 'Cannot save: No team members assigned. Please assign team members to roles before saving.'
        : `Failed to save configuration: ${err.message}. Please try again or contact support if the problem persists.`;
      setError(errorMsg);
      showNotification(errorMsg, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Load a configuration
  const handleLoadConfiguration = async (configId) => {
    const config = configurations.find(c => c.configuration_id === configId);
    if (!config) return;

    try {
      logger.debug('📂 Loading configuration:', config.configuration_name);

      // Get full configuration details
      const response = await apiGet(
        `team-optimization/configurations/${config.configuration_id}`
      );

      if (response.status === 'success') {
        const fullConfig = response.configuration;
        logger.debug('✅ Loaded full configuration:', fullConfig);

        // Notify parent to load this configuration
        if (onConfigurationLoad) {
          onConfigurationLoad(fullConfig);
        }

        setActiveConfigSlot(config.configuration_slot || 0);
        showNotification(`Configuration "${config.configuration_name}" loaded successfully`, 'success');
      } else {
        throw new Error(response.message || 'Load failed');
      }

    } catch (err) {
      logger.error('❌ Error loading configuration:', err);
      const errorMsg = err.message.includes('not found')
        ? `Configuration not found. It may have been deleted. Please refresh the page.`
        : `Failed to load configuration: ${err.message}. Please try again or contact support if the problem persists.`;
      setError(errorMsg);
      showNotification(errorMsg, 'error');
    }
  };

  // Soft Book configuration
  const handleSoftBook = async (slotNumber) => {
    const config = configurations.find(c => c.configuration_slot === slotNumber);
    if (!config) return;

    try {
      logger.debug('📝 Soft booking configuration:', config.configuration_name);

      const response = await apiFetch(
        `team-optimization/configurations/${config.configuration_id}/booking-status`,
        {
          method: 'PATCH',
          body: JSON.stringify({ booking_status: 'soft_booked' })
        }
      );

      const data = await response.json();

      logger.debug('Soft book API response:', data);

      if (!response.ok) {
        // Server returned an error
        throw new Error(data.error || data.message || `HTTP error! status: ${response.status}`);
      }

      if (data.status === 'success') {
        logger.debug('✅ Configuration soft booked successfully');
        showNotification(`Configuration "${config.configuration_name}" soft booked successfully`, 'success');

        // Clear Staffing Hub cache to force refresh
        localStorage.removeItem('staffingHub_data');

        // Reload configurations to show updated status
        await loadConfigurations();
        logger.debug('Configurations after reload:', configurations);
      } else {
        throw new Error(data.error || data.message || 'Soft booking failed');
      }

    } catch (err) {
      logger.error('❌ Error soft booking configuration:', err);
      setError(`Failed to soft book configuration: ${err.message}`);
      showNotification(`Failed to soft book: ${err.message}`, 'error');
    }
  };

  // Unbook (remove soft book) from configuration
  const handleUnbook = async (slotNumber) => {
    const config = configurations.find(c => c.configuration_slot === slotNumber);
    if (!config) return;

    try {
      logger.debug('🔓 Unbooking configuration:', config.configuration_name);

      const response = await apiFetch(
        `team-optimization/configurations/${config.configuration_id}/booking-status`,
        {
          method: 'PATCH',
          body: JSON.stringify({ booking_status: 'draft' })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || `HTTP error! status: ${response.status}`);
      }

      if (data.status === 'success') {
        logger.debug('✅ Configuration unbooked successfully');
        showNotification(`Configuration "${config.configuration_name}" unbooked successfully`, 'success');

        // Clear Staffing Hub cache to force refresh
        localStorage.removeItem('staffingHub_data');

        // Reload configurations to show updated status
        loadConfigurations();
      } else {
        throw new Error(data.error || data.message || 'Unbooking failed');
      }

    } catch (err) {
      logger.error('❌ Error unbooking configuration:', err);
      setError(`Failed to unbook configuration: ${err.message}`);
      showNotification(`Failed to unbook: ${err.message}`, 'error');
    }
  };

  // Delete a configuration
  const handleDeleteConfiguration = (configId) => {
    const config = configurations.find(c => c.configuration_id === configId);
    if (!config) return;
    setSlotToDelete(configId);
    setDeleteConfirmOpen(true);
  };

  const performDeleteConfiguration = async () => {
    const configId = slotToDelete;
    setDeleteConfirmOpen(false);
    setSlotToDelete(null);
    const config = configurations.find(c => c.configuration_id === configId);
    if (!config) return;

    try {
      // ✅ Enhanced logging: Show what we're about to delete
      logger.debug('🗑️ Deleting configuration:', {
        name: config.configuration_name,
        id: config.configuration_id,
        slot: config.configuration_slot,
        teamMembersCount: config.team_members?.length || 0,
        hasFullComponents: !!config.optimization_analysis?.full_components,
        hasDetailedAnalysis: !!config.detailed_analysis || !!config.claude_analysis,
        estimatedDataSize: JSON.stringify(config).length + ' characters'
      });

      const response = await apiFetch(
        `team-optimization/configurations/${config.configuration_id}`,
        {
          method: 'DELETE'
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'success') {
        logger.debug('✅ Configuration deleted successfully from database:', {
          configId: config.configuration_id,
          configName: config.configuration_name,
          deletedAt: new Date().toISOString()
        });

        // Immediately remove the configuration from state
        const configIndex = configurations.findIndex(c => c.configuration_id === configId);
        const updatedConfigurations = configurations.filter(c => c.configuration_id !== configId);
        setConfigurations(updatedConfigurations);

        // ✅ Clear comparison selection if this config was selected
        setSelectedForComparison(prev => prev.filter(index => index !== configIndex));

        showNotification(`Configuration "${config.configuration_name}" deleted successfully`, 'success');

        // Also reload from server to ensure consistency
        setTimeout(() => {
          loadConfigurations();
        }, 100);

      } else {
        throw new Error(data.message || 'Delete failed');
      }

    } catch (err) {
      logger.error('❌ Error deleting configuration:', err);
      const errorMsg = err.message.includes('not found')
        ? `Configuration not found. It may have already been deleted. Refreshing the page...`
        : err.message.includes('in use')
        ? `Cannot delete: Configuration is currently being used. Please unload it first.`
        : `Failed to delete configuration: ${err.message}. Please try again or contact support if the problem persists.`;
      setError(errorMsg);
      showNotification(errorMsg, 'error');
    }
  };

  // Compare configurations
  const handleCompareConfigurations = async () => {
    // Use selected configurations from checkboxes
    const selectedConfigs = selectedForComparison
      .map(index => configurations[index])
      .filter(config => config !== null);

    if (selectedConfigs.length < 2) {
      setError('Please select at least 2 configurations to compare using the checkboxes.');
      showNotification('Please select at least 2 configurations to compare', 'warning');
      return;
    }

    setIsComparing(true);
    setError(null);

    try {
      logger.debug('⚖️ Comparing configurations:', selectedConfigs.map(c => c.configuration_name));

      const configIds = selectedConfigs.map(config => config.configuration_id);

      const response = await apiFetch(
        'team-optimization/compare',
        {
          method: 'POST',
          body: JSON.stringify({ configuration_ids: configIds })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'success') {
        setComparisonResult(data.comparison);
        setComparisonDialogOpen(true);
        logger.debug('✅ Configuration comparison completed');
      } else {
        throw new Error(data.message || 'Comparison failed');
      }

    } catch (err) {
      logger.error('❌ Error comparing configurations:', err);
      setError(`Failed to compare configurations: ${err.message}`);
      showNotification(`Failed to compare: ${err.message}`, 'error');
    } finally {
      setIsComparing(false);
    }
  };

  // Get slot letter label (A, B, C, D, E, F, G, etc.)
  const getSlotLetter = (slotNumber) => {
    if (slotNumber === 0) return '★'; // Auto-saved draft
    // Convert slot number (1, 2, 3...) to letter (A, B, C...)
    return String.fromCharCode(64 + slotNumber); // 65 is 'A'
  };

  // Show notification
  const showNotification = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Get color based on value (higher = better for scores)
  const getScoreColor = (value) => {
    if (!value || value === 'N/A') return '#9e9e9e'; // Gray for no data
    if (value >= 7) return COLOR_GOOD; // Green for high (7-10)
    if (value >= 5) return '#ff9800'; // Yellow for medium (5-7)
    return COLOR_POOR; // Red for low (<5)
  };

  // Get color for margin percentage
  const getMarginColor = (margin) => {
    if (!margin) return '#9e9e9e';
    if (margin >= 50) return COLOR_GOOD; // Green for 50%+
    if (margin >= 40) return '#ff9800'; // Yellow for 40-50%
    return COLOR_POOR; // Red for <40%
  };

  // Generate default configuration name from team member last names
  const generateDefaultConfigName = () => {
    const teamMembers = getAssignedTeamMembers();
    if (teamMembers.length === 0) return '';

    const lastNames = teamMembers.slice(0, 5).map(member => {
      const nameParts = (member.employee_name || '').trim().split(' ');
      return nameParts[nameParts.length - 1]; // Get last name
    });

    return lastNames.join(' - ');
  };

  // Handle comparison checkbox toggle
  const handleComparisonToggle = (slotIndex) => {
    setSelectedForComparison(prev => {
      if (prev.includes(slotIndex)) {
        return prev.filter(i => i !== slotIndex);
      } else {
        return [...prev, slotIndex];
      }
    });
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // Handle viewing team member details
  const handleViewTeamMemberDetails = (member) => {
    // Convert team member data to candidate format for the modal
    const candidateData = {
      employee_id: member.employee_id,
      first_name: member.employee_name?.split(' ')[0] || 'Unknown',
      last_name: member.employee_name?.split(' ').slice(1).join(' ') || '',
      title: member.role,
      fit_score: member.fit_score,
      hourly_rate: member.hourly_rate ? `$${member.hourly_rate}` : 'N/A',
      // Add placeholder data for modal compatibility
      department: 'Unknown',
      years_experience: 'N/A',
      availability: { status: 'ASSIGNED' },
      dir_score: 'N/A',
      csot: 'N/A',
      csat: 'N/A',
      utilization: 'N/A',
      disc_profile: 'N/A',
      strengths: [],
      top_skills: [],
      skills: []
    };
    
    setSelectedTeamMember(candidateData);
    setTeamMemberDetailsOpen(true);
  };

  // Handle closing team member details modal
  const handleCloseTeamMemberDetails = () => {
    setTeamMemberDetailsOpen(false);
    setSelectedTeamMember(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading configurations...
        </Typography>
      </Box>
    );
  }

  const teamMembers = getAssignedTeamMembers();
  const hasTeam = teamMembers.length > 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Team Lineups
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CompareIcon />}
            onClick={handleCompareConfigurations}
            disabled={selectedForComparison.length < 2 || isComparing}
          >
            {isComparing ? 'Comparing...' : `Compare${selectedForComparison.length > 0 ? ` (${selectedForComparison.length})` : ''}`}
          </Button>
          <Tooltip title="Refresh Configurations">
            <IconButton onClick={loadConfigurations} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Current Team Summary Card (if has unsaved team) */}
      {hasTeam && !configurations.some(c => c !== null) && (
        <Card sx={{ mb: 3, bgcolor: BG_ELEVATED, border: '1px solid rgba(255,255,255,0.1)', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Current Team Composition
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {teamMembers.length} team members assigned • Not yet saved
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Configuration Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: BG_SURFACE }}>
              <TableCell padding="checkbox" sx={{ fontWeight: 'bold' }}>
                <Tooltip title="Select configurations to compare">
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Compare</Typography>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Lineup Name</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Overall</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Tech Coverage</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Team Chemistry</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Margin</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Availability</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Experience</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configurations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No saved configurations yet. Run a team analysis and save it to see it here.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              configurations.map((config, index) => {
                const slotNumber = config.configuration_slot;

              return (
                <TableRow
                  key={config.id || slotNumber}
                  sx={{
                    '&:hover': { bgcolor: 'rgba(123, 31, 162, 0.05)' }
                  }}
                >
                  {/* Compare Checkbox */}
                  <TableCell padding="checkbox">
                    {!config.is_partial_draft && (
                      <Checkbox
                        checked={selectedForComparison.includes(index)}
                        onChange={() => handleComparisonToggle(index)}
                          sx={{
                            color: 'rgba(255,255,255,0.4)',
                            '&.Mui-checked': {
                              color: '#fff',
                            },
                          }}
                        />
                    )}
                  </TableCell>

                  {/* Configuration Name */}
                  <TableCell>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {config.configuration_name}
                        </Typography>
                        {config.is_partial_draft && (
                          <Chip label="Draft" size="small" color="warning"
                            sx={{ fontWeight: 'bold', fontSize: '0.65rem', height: 18 }} />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {config.team_members?.length || 0} members • Saved {formatTimestamp(config.created_at)}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Overall Score */}
                  <TableCell align="center">
                    {config ? (() => {
                      const score = config.team_analysis_data?.scores?.overall ?? config.optimization_analysis?.overall_score ?? config.overall_score;
                      return (
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: getScoreColor(score) }}>
                          {typeof score === 'number' ? score.toFixed(1) : 'N/A'}
                        </Typography>
                      );
                    })() : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>

                  {/* Tech */}
                  <TableCell align="center">
                    {config ? (() => {
                      const score = config.team_analysis_data?.scores?.technical ?? config.optimization_analysis?.technical_coverage;
                      return (
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: getScoreColor(score) }}>
                          {typeof score === 'number' ? score.toFixed(1) : 'N/A'}
                        </Typography>
                      );
                    })() : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>

                  {/* Chemistry */}
                  <TableCell align="center">
                    {config ? (() => {
                      const score = config.team_analysis_data?.scores?.chemistry ?? config.optimization_analysis?.collaboration_score;
                      return (
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: getScoreColor(score) }}>
                          {typeof score === 'number' ? score.toFixed(1) : 'N/A'}
                        </Typography>
                      );
                    })() : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>

                  {/* Margin */}
                  <TableCell align="center">
                    {config ? (() => {
                      const marginPercentage = config.team_analysis_data?.financialView?.blendedMarginPct
                        ? parseFloat(config.team_analysis_data.financialView.blendedMarginPct)
                        : (config.margin_percentage || config.comparison_metrics?.margin_percentage || 0);
                      return (
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: getMarginColor(marginPercentage) }}>
                          {typeof marginPercentage === 'number' ? marginPercentage.toFixed(1) : '0.0'}%
                        </Typography>
                      );
                    })() : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>

                  {/* Availability */}
                  <TableCell align="center">
                    {config ? (() => {
                      const score = config.team_analysis_data?.scores?.availability;
                      return (
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: getScoreColor(score) }}>
                          {typeof score === 'number' ? score.toFixed(1) : 'N/A'}
                        </Typography>
                      );
                    })() : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>

                  {/* Experience */}
                  <TableCell align="center">
                    {config ? (() => {
                      const score = config.team_analysis_data?.scores?.experience ?? config.optimization_analysis?.experience_balance;
                      return (
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: getScoreColor(score) }}>
                          {typeof score === 'number' ? score.toFixed(1) : 'N/A'}
                        </Typography>
                      );
                    })() : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="center">
                    {(() => {
                      // Check if any configuration is already soft booked
                      const anySoftBooked = configurations.some(c => c.booking_status === 'soft_booked');
                      const isSoftBooked = config.booking_status === 'soft_booked';

                      // Debug logging
                      logger.debug(`Config ${slotNumber} - booking_status:`, config.booking_status,
                        'anySoftBooked:', anySoftBooked, 'isSoftBooked:', isSoftBooked);

                      return (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleLoadConfiguration(config.configuration_id)}
                          >
                            Load
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteConfiguration(config.configuration_id)}
                            sx={{ color: '#cf6679' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              );
            }))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Save Lineup Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Team Lineup</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Lineup Name"
            fullWidth
            variant="outlined"
            value={configurationName}
            onChange={(e) => setConfigurationName(e.target.value)}
            placeholder="e.g., Performance Optimized Team"
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel>Save to Slot</InputLabel>
            <Select
              value={activeConfigSlot}
              onChange={(e) => {
                const newSlot = e.target.value;
                logger.debug('📌 Slot changed to:', newSlot, '(Label:', getSlotLetter(newSlot), ')');
                setActiveConfigSlot(newSlot);
              }}
              label="Save to Slot"
            >
              {/* Show next 10 available slots */}
              {Array.from({ length: 10 }, (_, i) => i + 1).map((slotNumber) => {
                const existingConfig = configurations.find(c => c.configuration_slot === slotNumber);
                return (
                  <MenuItem key={slotNumber} value={slotNumber}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={getSlotLetter(slotNumber)}
                        size="small"
                        sx={{
                          bgcolor: existingConfig ? '#ff9800' : '#7b1fa2',
                          color: 'white',
                          fontSize: '0.7rem'
                        }}
                      />
                      {existingConfig ? (
                        <Typography variant="body2" color="warning.main">
                          {existingConfig.configuration_name} (Will overwrite)
                        </Typography>
                      ) : (
                        <Typography variant="body2">
                          Empty
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <InputLabel>Optimization Goal</InputLabel>
            <Select
              value={optimizationGoal}
              onChange={(e) => setOptimizationGoal(e.target.value)}
              label="Optimization Goal"
            >
              {optimizationGoals.map((goal) => (
                <MenuItem key={goal.value} value={goal.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {goal.icon}
                    {goal.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Saving current team ({teamMembers.length} members) to Slot {getSlotLetter(activeConfigSlot)}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveConfiguration}
            variant="contained"
            disabled={isSaving || !configurationName.trim()}
            sx={{ bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#6a1b9a' } }}
          >
            {isSaving ? 'Saving...' : 'Save Lineup'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Overwrite Confirmation Dialog */}
      <Dialog open={overwriteConfirmOpen} onClose={() => setOverwriteConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Overwrite</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Slot {getSlotLetter(slotToOverwrite || 1)} already contains "{configurations.find(c => c.configuration_slot === slotToOverwrite)?.configuration_name}".
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to overwrite this configuration? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The existing configuration will be permanently deleted and replaced with your current team setup.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverwriteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              setOverwriteConfirmOpen(false);

              // First delete the existing configuration
              const configToDelete = configurations[slotToOverwrite];
              if (configToDelete) {
                try {
                  const response = await apiFetch(
                    `team-optimization/configurations/${configToDelete.configuration_id}`,
                    { method: 'DELETE' }
                  );

                  if (!response.ok) {
                    throw new Error('Failed to delete existing configuration');
                  }
                } catch (err) {
                  logger.error('❌ Error deleting configuration before overwrite:', err);
                  setError(`Failed to overwrite: ${err.message}`);
                  showNotification(`Failed to overwrite: ${err.message}`, 'error');
                  return;
                }
              }

              // Then save the new configuration
              await performSaveConfiguration();
            }}
            variant="contained"
            color="warning"
            disabled={isSaving}
          >
            {isSaving ? 'Overwriting...' : 'Yes, Overwrite'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Lineup</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            "{configurations.find(c => c.configuration_slot === slotToDelete)?.configuration_name}" will be permanently deleted.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={performDeleteConfiguration} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comparison Dialog */}
      <Dialog open={comparisonDialogOpen} onClose={() => setComparisonDialogOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Lineup Comparison
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {comparisonResult && (() => {
            const selectedConfigs = selectedForComparison
              .map(index => configurations[index])
              .filter(Boolean);
            const comparisonData = generateComparisonData(selectedConfigs);
            return (
              <AIDecisionAdvisor
                comparisonData={comparisonData}
                selectedConfigIds={selectedConfigs.map(c => c.configuration_id)}
                onClose={() => setComparisonDialogOpen(false)}
                opportunityMeta={{
                  clientName: staffingData?.clientName,
                  closeDate: staffingData?.estimatedCloseDate,
                  startDate: staffingData?.start_date,
                  winProbability: staffingData?.winProbability,
                }}
              />
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComparisonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Team Member Details Modal */}
      <CandidateDetailsModal
        open={teamMemberDetailsOpen}
        onClose={handleCloseTeamMemberDetails}
        candidate={selectedTeamMember}
      />

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

TeamConfigurationManager.propTypes = {
  staffingData: PropTypes.object.isRequired,
  optimizationResult: PropTypes.object,
  onConfigurationLoad: PropTypes.func
};

export default TeamConfigurationManager;
